import { Phone, MessageCircle, MapPin, Clock, Mail } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";

const PHONE = "tel:+41766388655";
const WHATSAPP = "https://wa.me/41766388655?text=Bonjour%2C%20je%20souhaite%20un%20devis.";

const MAP_SRC = "https://maps.google.com/maps?q=Rue+Voltaire+22,+1201+Gen%C3%A8ve,+Suisse&output=embed";

const LazyMap = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setShow(true); observer.disconnect(); } },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="glass-card rounded-lg overflow-hidden max-w-4xl mx-auto mb-16">
      {show ? (
        <iframe
          title="ProImpact Carrosserie Genève - Rue Voltaire 22, 1201 Genève"
          src={MAP_SRC}
          className="w-full h-64 md:h-80 border-0"
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        <div className="w-full h-64 md:h-80 bg-muted animate-pulse" />
      )}
    </div>
  );
};

const services = [
  { label: "Tôlerie & Peinture", href: "/services/tolerie-peinture" },
  { label: "Pare-brise", href: "/services/pare-brise" },
  { label: "Gestion Sinistres Assurance", href: "/services/gestion-sinistre" },
];

const zones: { label: string; href?: string }[] = [
  { label: "Genève Centre" },
  { label: "Carouge", href: "/carrosserie-carouge" },
  { label: "Vernier", href: "/carrosserie-vernier" },
  { label: "Meyrin", href: "/carrosserie-meyrin" },
  { label: "Lancy", href: "/carrosserie-lancy" },
  { label: "Satigny" },
  { label: "Grand-Saconnex" },
  { label: "Onex" },
  { label: "Tout le canton de Genève" },
];

const seoLinks = [
  { label: "Carrosserie Genève", href: "/services" },
  { label: "Carrossier Genève", href: "/services/tolerie-peinture" },
  { label: "Réparation bosses et grêle Genève", href: "/services/tolerie-peinture" },
  { label: "Peinture voiture Genève", href: "/services/tolerie-peinture" },
  { label: "Pare-brise Genève", href: "/services/pare-brise" },
  { label: "Gestion sinistre assurance Genève", href: "/services/gestion-sinistre" },
];

const FooterSection = () => (
  <footer id="contact" className="py-24 border-t border-border/50">
    <div className="container mx-auto px-4">
      {/* CTAs */}
      <div className="text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
          Un dégât à faire réparer ?
        </h2>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto">
          Appelez l'atelier ou envoyez des photos sur WhatsApp. Devis gratuit sous 24 h.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center" data-cta-zone>
          <Button size="lg" className="gap-3 text-base h-14 px-10" asChild>
            <a href={PHONE}>
              <Phone className="w-5 h-5" /> Appeler l'atelier
            </a>
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="gap-3 text-base h-14 px-10 border-primary/30 text-primary hover:bg-primary/10"
            asChild
          >
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="w-5 h-5" /> WhatsApp
            </a>
          </Button>
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto mb-16">
        <address className="glass-card rounded-lg p-6 text-center not-italic">
          <MapPin className="w-6 h-6 text-primary mx-auto mb-3" />
          <h3 className="font-semibold mb-1">Adresse</h3>
          <p className="text-sm text-muted-foreground">
            Rue Voltaire 22<br />1201 Genève, Suisse
          </p>
        </address>
        <div className="glass-card rounded-lg p-6 text-center">
          <Clock className="w-6 h-6 text-primary mx-auto mb-3" />
          <h3 className="font-semibold mb-1">Horaires</h3>
          <p className="text-sm text-muted-foreground">
            Ouvert 24h/24 &ndash; 7j/7
          </p>
        </div>
        <div className="glass-card rounded-lg p-6 text-center">
          <Phone className="w-6 h-6 text-primary mx-auto mb-3" />
          <h3 className="font-semibold mb-1">Téléphone</h3>
          <p className="text-sm text-muted-foreground">
            <a href={PHONE} className="hover:text-primary transition-colors">+41 76 638 86 55</a>
          </p>
        </div>
        <div className="glass-card rounded-lg p-6 text-center">
          <Mail className="w-6 h-6 text-primary mx-auto mb-3" />
          <h3 className="font-semibold mb-1">Email</h3>
          <p className="text-sm text-muted-foreground">
            <a href="mailto:contact@proimpact.ch" className="hover:text-primary transition-colors">contact@proimpact.ch</a>
          </p>
        </div>
      </div>

      {/* Map - lazy loaded */}
      <LazyMap />

      {/* 4-Column SEO Footer */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-16 border-t border-border/50 pt-16">
        {/* Column 1: About */}
        <div>
          <div className="mb-4">
            <Logo size="lg" />
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            ProImpact Carrosserie est votre spécialiste en tôlerie, peinture haute fidélité et pare-brise à Genève. Gestion assurance complète, prise en charge dans tout le canton : on vous rend la voiture là où on l'a prise. Devis gratuit sous 24h.
          </p>
        </div>

        {/* Column 2: Services */}
        <div>
          <h4 className="font-semibold mb-4 text-sm">Nos services</h4>
          <ul className="space-y-2">
            {services.map((s) => (
              <li key={s.label}>
                <a href={s.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 3: Zones */}
        <div>
          <h4 className="font-semibold mb-4 text-sm">Zones desservies</h4>
          <ul className="space-y-2">
            {zones.map((z) => (
              <li key={z.label} className="text-sm text-muted-foreground">
                {z.href ? (
                  <Link to={z.href} className="hover:text-foreground transition-colors">{z.label}</Link>
                ) : (
                  z.label
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Column 4: Links */}
        <div>
          <h4 className="font-semibold mb-4 text-sm">Informations</h4>
          <ul className="space-y-2">
            <li>
              <Link to="/services" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Nos services
              </Link>
            </li>
            <li>
              <Link to="/a-propos" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                À propos
              </Link>
            </li>
            <li>
              <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Blog carrosserie
              </Link>
            </li>
            <li>
              <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Demander un devis
              </Link>
            </li>
            <li>
              <Link to="/mentions-legales" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Mentions légales
              </Link>
            </li>
            <li>
              <Link to="/politique-confidentialite" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Politique de confidentialité
              </Link>
            </li>
            <li>
              <Link to="/plan-du-site" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Plan du site
              </Link>
            </li>
            <li>
              <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Contact
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border/50 pt-8 text-center">
        <p className="text-xs text-muted-foreground mb-4">
          &copy; {new Date().getFullYear()} ProImpact Carrosserie Genève. Tous droits réservés. ·{" "}
          <a href="https://kollowo.com" target="_blank" rel="noopener" className="hover:text-foreground transition-colors">
            Conçu par Kollowo
          </a>
        </p>
        <nav aria-label="Liens SEO" className="flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
          {seoLinks.map(({ label, href }) => (
            <a key={label} href={href} className="hover:text-foreground transition-colors">
              {label}
            </a>
          ))}
        </nav>
      </div>
    </div>
  </footer>
);

export default FooterSection;
